import { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, HelpCircle, Search, MessageCircle, Mail, Phone, Clock } from 'lucide-react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

// Styled Components
const FAQContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding-top: 80px;
`;

const Section = styled.section`
  padding: 4rem 0;
  
  @media (max-width: 768px) {
    padding: 2rem 0;
  }
`;

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 1rem;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  
  h1 {
    font-size: clamp(2rem, 5vw, 3rem);
    font-weight: 800;
    color: #1a202c;
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.125rem;
    color: #64748b;
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.6;
  }
`;

const SearchBar = styled.div`
  max-width: 600px;
  margin: 0 auto 3rem;
  position: relative;
  
  .search-input {
    width: 100%;
    padding: 1rem 3rem 1rem 1rem;
    border: 1px solid #e5e7eb;
    border-radius: 2rem;
    font-size: 1rem;
    background: white;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    transition: all 0.2s ease;
    
    &:focus {
      outline: none;
      border-color: #3b82f6;
      box-shadow: 0 4px 6px rgba(59, 130, 246, 0.2);
    }
  }
  
  .search-icon {
    position: absolute;
    right: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: #64748b;
  }
`;

const CategoryTabs = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 3rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 0.5rem;
  }
  
  .tab {
    padding: 0.75rem 1.5rem;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 2rem;
    cursor: pointer;
    transition: all 0.2s ease;
    font-weight: 500;
    color: #374151;
    
    &:hover {
      border-color: #3b82f6;
      color: #3b82f6;
    }
    
    &.active {
      background: linear-gradient(135deg, #3b82f6, #2563eb);
      color: white;
      border-color: #3b82f6;
    }
    
    @media (max-width: 768px) {
      padding: 0.5rem 1rem;
      font-size: 0.875rem;
    }
  }
`;

const FAQSection = styled.div`
  margin-bottom: 3rem;
  
  h2 {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1a202c;
    margin-bottom: 1.5rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`;

const FAQItem = styled.div`
  background: white;
  border-radius: 1rem;
  margin-bottom: 1rem;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  .faq-question {
    padding: 1.5rem;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-weight: 600;
    color: #1a202c;
    transition: all 0.2s ease;
    
    &:hover {
      background: #f8fafc;
    }
    
    .icon {
      transition: transform 0.3s ease;
      color: #3b82f6;
    }
    
    &.open .icon {
      transform: rotate(180deg);
    }
  }
  
  .faq-answer {
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.3s ease;
    
    &.open {
      max-height: 500px;
    }
    
    .answer-content {
      padding: 0 1.5rem 1.5rem;
      color: #64748b;
      line-height: 1.6;
      
      p {
        margin-bottom: 0.5rem;
      }
      
      ul {
        margin-left: 1.5rem;
        margin-bottom: 0.5rem;
      }
      
      .highlight {
        color: #3b82f6;
        font-weight: 600;
      }
    }
  }
`;

const ContactSection = styled.div`
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  border-radius: 1rem;
  padding: 2rem;
  color: white;
  text-align: center;
  margin-top: 3rem;
  
  h3 {
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 1rem;
  }
  
  p {
    margin-bottom: 1.5rem;
    opacity: 0.9;
  }
  
  .contact-buttons {
    display: flex;
    justify-content: center;
    gap: 1rem;
    flex-wrap: wrap;
    
    .contact-btn {
      background: white;
      color: #3b82f6;
      border: none;
      border-radius: 0.5rem;
      padding: 0.75rem 1.5rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      text-decoration: none;
      
      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
      }
    }
  }
`;

const FAQPage = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('general');
  const [searchTerm, setSearchTerm] = useState('');
  const [openItems, setOpenItems] = useState(new Set());

  const categories = [
    { id: 'general', name: 'Général' },
    { id: 'products', name: 'Produits' },
    { id: 'orders', name: 'Commandes' },
    { id: 'payment', name: 'Paiement' },
    { id: 'shipping', name: 'Livraison' },
    { id: 'returns', name: 'Retours' },
    { id: 'technical', name: 'Support technique' }
  ];

  const faqData = {
    general: [
      {
        question: "Qu'est-ce qu'INZA STORE ?",
        answer: "INZA STORE est une boutique spécialisée dans la vente de produits Apple authentiques en Côte d'Ivoire. Nous proposons des iPhones, iPads, MacBooks et accessoires avec garantie et service client premium."
      },
      {
        question: "Où êtes-vous situé ?",
        answer: "Notre boutique est située à Abidjan, Plateau, face à la BCEAO, dans l'Immeuble Le Concorde au 2ème étage. Nous sommes ouverts du lundi au vendredi de 8h à 18h et le samedi de 9h à 15h."
      },
      {
        question: "Vos produits sont-ils garantis ?",
        answer: "Oui, tous nos produits bénéficient d'une garantie de 2 ans. Nous proposons également des extensions de garantie pour une tranquillité d'esprit totale."
      }
    ],
    products: [
      {
        question: "Vos iPhones sont-ils neufs ?",
        answer: "Nous proposons des iPhones neufs et reconditionnés certifiés. Chaque produit est soigneusement testé et vient avec une description détaillée de son état."
      },
      {
        question: "Proposez-vous des accessoires ?",
        answer: "Oui, nous avons une large gamme d'accessoires Apple : coques, chargeurs, écouteurs, câbles, et bien plus. Tous nos accessoires sont authentiques."
      },
      {
        question: "Puis-je tester les produits en boutique ?",
        answer: "Absolument ! Notre boutique dispose d'espaces de démonstration où vous pouvez tester tous nos produits avant achat."
      }
    ],
    orders: [
      {
        question: "Comment passer une commande ?",
        answer: "Vous pouvez commander en ligne via notre site web ou directement en boutique. Pour les commandes en ligne, nous vous guiderons à chaque étape du processus."
      },
      {
        question: "Puis-je modifier ma commande ?",
        answer: "Les commandes peuvent être modifiées dans les 2 heures suivant leur confirmation. Après ce délai, contactez directement notre service client."
      },
      {
        question: "Comment suivre ma commande ?",
        answer: "Une fois votre commande expédiée, vous recevrez un email avec un numéro de suivi. Vous pouvez également suivre votre commande depuis votre compte client."
      }
    ],
    payment: [
      {
        question: "Quels modes de paiement acceptez-vous ?",
        answer: "Nous acceptons les paiements par carte bancaire, mobile money (Orange Money, MTN Money, MoMo), virement bancaire, et espèces en boutique. Tous les paiements sont sécurisés."
      },
      {
        question: "Puis-je payer en plusieurs fois ?",
        answer: "Oui, nous proposons des options de paiement échelonné pour les achats supérieurs à 500 000 XOF. Contactez-nous pour plus d'informations."
      },
      {
        question: "Mes informations de paiement sont-elles sécurisées ?",
        answer: "Absolument. Nous utilisons des technologies de cryptage de pointe pour protéger toutes vos informations financières."
      }
    ],
    shipping: [
      {
        question: "Quels sont les délais de livraison ?",
        answer: "À Abidjan : 24-48h. En intérieur : 2-5 jours ouvrables. Les commandes passées avant 14h sont expédiées le jour même."
      },
      {
        question: "Quels sont les frais de livraison ?",
        answer: "La livraison est gratuite à Abidjan pour les commandes de plus de 100 000 XOF. En intérieur, les frais varient selon la localité."
      },
      {
        question: "Puis-je récupérer ma commande en boutique ?",
        answer: "Oui, le retrait en boutique est disponible et gratuit. Votre commande sera réservée pendant 7 jours."
      }
    ],
    returns: [
      {
        question: "Quelle est votre politique de retour ?",
        answer: "Vous avez 14 jours pour retourner un produit si vous n'êtes pas satisfait. Le produit doit être en état neuf et dans son emballage d'origine."
      },
      {
        question: "Comment effectuer un retour ?",
        answer: "Contactez notre service client pour obtenir une autorisation de retour. Nous vous fournirons une étiquette de retour gratuite."
      },
      {
        question: "Quand serai-je remboursé ?",
        answer: "Une fois le retour reçu et vérifié, le remboursement est traité dans les 5-7 jours ouvrables."
      }
    ],
    technical: [
      {
        question: "Proposez-vous un support technique ?",
        answer: "Oui, notre équipe technique est disponible pour vous aider avec la configuration, l'utilisation et le dépannage de vos produits Apple."
      },
      {
        question: "Faites-vous des réparations ?",
        answer: "Nous proposons des services de réparation pour tous les produits Apple. Contactez notre SAV pour un devis personnalisé."
      },
      {
        question: "Proposez-vous des formations ?",
        answer: "Oui, nous organisons régulièrement des ateliers gratuits pour vous aider à maîtriser vos appareils Apple."
      }
    ]
  };

  const toggleItem = (index) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
    } else {
      newOpenItems.add(index);
    }
    setOpenItems(newOpenItems);
  };

  const filteredFAQs = faqData[activeCategory]?.filter(item =>
    item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.answer.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  return (
    <FAQContainer>
      <Section>
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <PageHeader>
              <h1>FAQ - Questions Fréquentes</h1>
              <p>Trouvez rapidement les réponses à vos questions sur nos produits et services.</p>
            </PageHeader>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <SearchBar>
              <input
                type="text"
                className="search-input"
                placeholder="Rechercher une question..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Search className="search-icon" size={20} />
            </SearchBar>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <CategoryTabs>
              {categories.map(category => (
                <button
                  key={category.id}
                  className={`tab ${activeCategory === category.id ? 'active' : ''}`}
                  onClick={() => setActiveCategory(category.id)}
                >
                  {category.name}
                </button>
              ))}
            </CategoryTabs>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <FAQSection>
              <h2>
                <HelpCircle size={24} />
                {categories.find(cat => cat.id === activeCategory)?.name}
              </h2>
              
              {filteredFAQs.length > 0 ? (
                filteredFAQs.map((item, index) => (
                  <FAQItem key={index}>
                    <div
                      className={`faq-question ${openItems.has(index) ? 'open' : ''}`}
                      onClick={() => toggleItem(index)}
                    >
                      {item.question}
                      <ChevronDown className="icon" size={20} />
                    </div>
                    <div className={`faq-answer ${openItems.has(index) ? 'open' : ''}`}>
                      <div className="answer-content">
                        <p>{item.answer}</p>
                      </div>
                    </div>
                  </FAQItem>
                ))
              ) : (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                  Aucune question trouvée pour votre recherche.
                </div>
              )}
            </FAQSection>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <ContactSection>
              <h3>Vous ne trouvez pas votre réponse ?</h3>
              <p>Notre équipe de support est là pour vous aider</p>
              <div className="contact-buttons">
                <a href="/contact" className="contact-btn" onClick={(e) => { e.preventDefault(); navigate('/contact'); }}>
                  <Mail size={18} />
                  Nous contacter
                </a>
                <a href="/sav" className="contact-btn" onClick={(e) => { e.preventDefault(); navigate('/sav'); }}>
                  <Phone size={18} />
                  SAV
                </a>
                <a href="tel:+22520000000" className="contact-btn">
                  <Phone size={18} />
                  Appeler
                </a>
              </div>
            </ContactSection>
          </motion.div>
        </Container>
      </Section>
    </FAQContainer>
  );
};

export default FAQPage;
